import { PurchaseOrderDeliveryStatusEnum, PurchaseOrderInvoicedStatusEnum } from "@finway-group/shared/lib/models"
import { formatCurrencyNumber } from "@finway-group/shared/lib/utils"
import { Avatar, Button, TableColumnType } from "antd"
import moment from "moment"
import React from "react"

import { CaretDownOutlined } from "@ant-design/icons"
import { PurchaseOrderInvoicedStatusTag } from "Features/pages/purchaseOrders/components/PurchaseOrderInvoicedStatusTag"
import { PurchaseOrderOptionButtons } from "Features/pages/purchaseOrders/components/PurchaseOrderOptionButtons"
import { PurchaseOrderStatusTag } from "Features/pages/purchaseOrders/components/PurchaseOrderStatusTag"
import { PurchaseOrder, PurchaseOrderTableColumn } from "../types/PurchaseOrder.interfaces"

import { PurchaseOrderDeliveryStatusTag } from "../components/PurchaseOrderDeliveryStatusTag"
import { IsServiceIcon } from "../icons/IsServiceIcon"
import { ShoppingBagIcon } from "../icons/ShoppingBagIcon"
import { GenerateColumnsProps } from "../types/utils/helper.util"

export const enum PurchaseOrderColumns {
    NAME = "name",
    VENDOR = "vendor",
    REQUESTER = "requester",
    DATE = "dateOfIssue",
    DELIVERY_STATUS = "deliveryStatus",
    INVOICED_STATUS = "invoicedStatus",
    AMOUNT = "amount",
    STATUS = "status",
    ACTION = "action",
}

export const purchaseOrderColumnMapper = {
    [PurchaseOrderColumns.NAME]: "purchaseOrderNumber",
    [PurchaseOrderColumns.VENDOR]: "vendor.name",
    [PurchaseOrderColumns.REQUESTER]: "requestedBy",
    [PurchaseOrderColumns.DATE]: "dateOfIssue",
    [PurchaseOrderColumns.DELIVERY_STATUS]: "deliveryStatus",
    [PurchaseOrderColumns.INVOICED_STATUS]: "invoicedStatus",
    [PurchaseOrderColumns.AMOUNT]: "totalGrossPrice",
    [PurchaseOrderColumns.STATUS]: "state.currentState",
}

export const GeneratePurchaseOrderColumns = ({
    updateRecord,
    deleteRecord,
    getRecord,
    t,
}: GenerateColumnsProps<PurchaseOrderTableColumn>): Array<TableColumnType<PurchaseOrderTableColumn>> => [
    {
        title: t("purchaseOrders:columns.name"),
        dataIndex: PurchaseOrderColumns.NAME,
        key: PurchaseOrderColumns.NAME,
        fixed: "left" as const,
        width: 250,
        render: (_: unknown, record: PurchaseOrderTableColumn) => (
            <div className="flex items-center gap-8 overflow-hidden">
                <Avatar
                    src={"#"}
                    icon={record.isService ? <IsServiceIcon /> : <ShoppingBagIcon />}
                    size={38}
                    style={{
                        background: "rgba(111, 186, 255, 0.10)",
                    }}
                    className="flex-shrink-0 bg-[rgba(111,186,255,0.10)] flex rounded-full border-none justify-center items-center"
                />
                <div className="flex-1 overflow-hidden">
                    <div className="font-bold text-15 text-[#181c32] overflow-hidden text-ellipsis whitespace-no-wrap">{record.name}</div>
                    <div className="text-15 text-[#6B7280] overflow-hidden text-ellipsis whitespace-no-wrap font-normal">{record.description}</div>
                </div>
            </div>
        ),
        sorter: true,
    },
    {
        title: t("purchaseOrders:columns.vendor"),
        dataIndex: PurchaseOrderColumns.VENDOR,
        key: PurchaseOrderColumns.VENDOR,
        width: 150,
        sorter: true,
        render: (vendor: string) => <span className="font-medium text-15">{vendor}</span>,
    },
    {
        title: t("purchaseOrders:columns.requester"),
        dataIndex: PurchaseOrderColumns.REQUESTER,
        key: PurchaseOrderColumns.REQUESTER,
        width: 150,
        sorter: true,
        render: (requester: string) => <span className="font-medium text-15">{requester}</span>,
    },
    {
        title: t("purchaseOrders:columns.dateOfIssue"),
        dataIndex: PurchaseOrderColumns.DATE,
        key: PurchaseOrderColumns.DATE,
        width: 100,
        ellipsis: true,
        sorter: true,
        render: (date: Date) => <span className="font-medium text-15">{moment(date).format("MMM D, YYYY")}</span>,
    },
    {
        title: t("purchaseOrders:columns.deliveryStatus"),
        dataIndex: PurchaseOrderColumns.DELIVERY_STATUS,
        key: PurchaseOrderColumns.DELIVERY_STATUS,
        width: 120,
        ellipsis: true,
        sorter: true,
        render: (deliveryStatus: PurchaseOrderDeliveryStatusEnum) => <PurchaseOrderDeliveryStatusTag status={deliveryStatus} short />,
    },
    {
        title: t("purchaseOrders:columns.invoicedStatus"),
        dataIndex: PurchaseOrderColumns.INVOICED_STATUS,
        key: PurchaseOrderColumns.INVOICED_STATUS,
        width: 120,
        ellipsis: true,
        sorter: true,
        render: (invoicedStatus: PurchaseOrderInvoicedStatusEnum) => <PurchaseOrderInvoicedStatusTag status={invoicedStatus} short />,
    },
    {
        title: t("purchaseOrders:columns.amount"),
        dataIndex: "amount",
        key: "amount",
        width: 100,
        ellipsis: true,
        sorter: true,
        render: (amount: string, record) => <span className="font-bold text-15">{formatCurrencyNumber({ value: amount, currency: record.currency })}</span>,
    },
    {
        title: t("purchaseOrders:columns.status"),
        dataIndex: PurchaseOrderColumns.STATUS,
        key: PurchaseOrderColumns.STATUS,
        width: 120,
        ellipsis: true,
        sorter: true,
        render: (status: PurchaseOrder["state"]) => <PurchaseOrderStatusTag status={status.currentState} />,
    },

    {
        title: t("purchaseOrders:columns.action"),
        key: PurchaseOrderColumns.ACTION,
        width: 120,
        align: "right",
        fixed: "right" as const,
        render: (_: unknown, record: PurchaseOrderTableColumn) => (
            <PurchaseOrderOptionButtons record={record} onEdit={(rec) => updateRecord(rec)} onDelete={(rec) => deleteRecord(rec.key)} onView={(rec) => getRecord(rec)}>
                <Button className="ant-btn-primary" onClick={(e) => e.stopPropagation()}>
                    {t("label:options")}
                    <CaretDownOutlined className="text-sm" />
                </Button>
            </PurchaseOrderOptionButtons>
        ),
    },
]
